import React, { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { IOSInstallBanner } from "@/components/app/IOSInstallBanner";
import { DesktopInstallPrompt } from "@/components/app/DesktopInstallPrompt";
import { SecondOpenPushPrompt } from "@/components/app/PushNudges";
import { AdSenseLoader } from "@/components/app/AdSenseLoader";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CallProvider } from "@/context/CallContext";
import { LightboxProvider } from "@/context/LightboxContext";
import CallOverlay from "@/components/app/CallOverlay";
import DesktopFrame from "@/components/app/DesktopFrame";
import DesktopSidebar from "@/components/app/DesktopSidebar";
import DesktopRightRail from "@/components/app/DesktopRightRail";
import WelcomeTour, { hasSeenTour } from "@/components/app/WelcomeTour";
import { onPushNav } from "@/lib/push";
import useWakeLock from "@/hooks/useWakeLock";

// Eager-loaded (critical first-paint routes): the marketing landing, demo
// auto-login, auth screens, and the primary "logged-in home" (Discover) so
// they render without a Suspense flash.
import Landing from "@/pages/LandingTonight";
import AuthPage from "@/pages/Auth";
import Demo from "@/pages/Demo";
import AuthCallback from "@/pages/AuthCallback";
import Discover from "@/pages/Discover";

// Everything else is code-split — the chunks are fetched on demand the first
// time a user navigates to that route. Cuts initial bundle dramatically.
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Chats = lazy(() => import("@/pages/Chats"));
const Chat = lazy(() => import("@/pages/Chat"));
const ChatsWorkspace = lazy(() => import("@/pages/ChatsWorkspace"));
const Groups = lazy(() => import("@/pages/Groups"));
const GroupChat = lazy(() => import("@/pages/GroupChat"));
const Profile = lazy(() => import("@/pages/Profile"));
const Upgrade = lazy(() => import("@/pages/Upgrade"));
const Likes = lazy(() => import("@/pages/Likes"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));
const Events = lazy(() => import("@/pages/Events"));
const FriendChat = lazy(() => import("@/pages/FriendChat"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const AUP = lazy(() => import("@/pages/AUP"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Refund = lazy(() => import("@/pages/Refund"));
const LawEnforcement = lazy(() => import("@/pages/LawEnforcement"));
const AgePolicy = lazy(() => import("@/pages/AgePolicy"));
const ChildSafety = lazy(() => import("@/pages/ChildSafety"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const Tour = lazy(() => import("@/pages/Tour"));
const About = lazy(() => import("@/pages/About"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Tonight = lazy(() => import("@/pages/Tonight"));
const TopPicks = lazy(() => import("@/pages/TopPicks"));
const LandingMembersOnly = lazy(() => import("@/pages/LandingMembersOnly"));
const LandingManifesto = lazy(() => import("@/pages/LandingManifesto"));
const LandingMagazine = lazy(() => import("@/pages/LandingMagazine"));
const LandingCards = lazy(() => import("@/pages/LandingCards"));
const LandingTonight = lazy(() => import("@/pages/LandingTonight"));
const LandingPicker = lazy(() => import("@/pages/LandingPicker"));
const AdminDomain = lazy(() => import("@/pages/AdminDomain"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Referrals = lazy(() => import("@/pages/Referrals"));
const Viewers = lazy(() => import("@/pages/Viewers"));
const ShareDeck = lazy(() => import("@/pages/ShareDeck"));
const Moments = lazy(() => import("@/pages/Moments"));
const NewMoment = lazy(() => import("@/pages/NewMoment"));
const Verify = lazy(() => import("@/pages/Verify"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/discover" replace />;
  return children;
}

// Minimal Suspense fallback — brand-aligned, replaces blank flash with a
// short loader so route transitions feel intentional, not broken.
function RouteFallback() {
  return (
    <div
      data-testid="route-fallback"
      className="min-h-[60svh] flex items-center justify-center bg-[#16171A]"
    >
      <Loader2 className="w-6 h-6 animate-spin text-[#C9A961]" />
    </div>
  );
}

function Shell() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [tourOpen, setTourOpen] = useState(false);

  // Keep the device screen awake everywhere in Spark — signed in or not
  // (landing, login, tour, chat, moments). The hook re-acquires the lock on
  // tab return and after the first user gesture.
  useWakeLock(true);

  useEffect(() => {
    // When a Web Push notification is clicked, the service worker postMessages
    // us the deep link to navigate to (e.g. /chats/<matchId>). Use the router
    // so it doesn't force a full reload.
    return onPushNav((url) => {
      try {
        nav(url);
      } catch {
        /* fallback handled by service worker openWindow */
      }
    });
  }, [nav]);

  // Auto-open the WelcomeTour the first time a user signs in. Demo personas
  // are skipped — we don't want the tour to fire every time a visitor
  // re-opens a /demo?as=... link.
  useEffect(() => {
    if (!user || hasSeenTour()) return;
    const isDemo = (user.email || "").endsWith("@spark.demo");
    if (isDemo) return;
    setTourOpen(true);
  }, [user]);

  // Profile "Take the tour" button dispatches this event — keeps the tour
  // launchable from anywhere without prop-drilling.
  useEffect(() => {
    const onReplay = () => setTourOpen(true);
    window.addEventListener("spark:open-tour", onReplay);
    return () => window.removeEventListener("spark:open-tour", onReplay);
  }, []);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/auth-callback" element={<AuthCallback />} />
        <Route path="/share" element={<ShareDeck />} />
        <Route path="/share-deck" element={<ShareDeck />} />
        <Route path="/v1" element={<LandingMembersOnly />} />
        <Route path="/v2" element={<LandingManifesto />} />
        <Route path="/v3" element={<LandingMagazine />} />
        <Route path="/v4" element={<LandingCards />} />
        <Route path="/v5" element={<LandingTonight />} />
        <Route path="/pick" element={<LandingPicker />} />
        <Route path="/login" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
        <Route path="/signup" element={<PublicOnly><AuthPage mode="signup" /></PublicOnly>} />
        {/* /register is the share-link target (e.g. https://sparkdatin.com/register?ref=ALI-7K3Q).
            It aliases /signup but preserves the query string so referral codes survive. */}
        <Route path="/register" element={<PublicOnly><AuthPage mode="signup" /></PublicOnly>} />
        <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
        <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
        <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
        <Route path="/discover" element={<Protected><Discover /></Protected>} />
        <Route path="/chats" element={<Protected><ChatsWorkspace /></Protected>} />
        <Route path="/chats/:matchId" element={<Protected><ChatsWorkspace /></Protected>} />
        <Route path="/groups" element={<Protected><Groups /></Protected>} />
        <Route path="/groups/:groupId" element={<Protected><GroupChat /></Protected>} />
        <Route path="/events" element={<Protected><Events /></Protected>} />
        <Route path="/tonight" element={<Protected><Tonight /></Protected>} />
        <Route path="/top-picks" element={<Protected><TopPicks /></Protected>} />
        <Route path="/friends/:fid" element={<Protected><FriendChat /></Protected>} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/upgrade" element={<Protected><Upgrade /></Protected>} />
        <Route path="/likes" element={<Protected><Likes /></Protected>} />
        <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/aup" element={<AUP />} />
        <Route path="/acceptable-use" element={<AUP />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/plans" element={<Pricing />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/refund-policy" element={<Refund />} />
        <Route path="/law-enforcement" element={<LawEnforcement />} />
        <Route path="/legal/law-enforcement" element={<LawEnforcement />} />
        <Route path="/age-policy" element={<AgePolicy />} />
        <Route path="/18-plus" element={<AgePolicy />} />
        <Route path="/child-safety" element={<ChildSafety />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/tour" element={<Tour />} />
        <Route path="/tour-pro" element={<Tour />} />
        <Route path="/cine-tour" element={<Navigate to="/tour-pro" replace />} />
        <Route path="/demo-tour" element={<Tour />} />
        <Route path="/about" element={<About />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/admin/domain" element={<Protected><AdminDomain /></Protected>} />
        <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
        <Route path="/referrals" element={<Protected><Referrals /></Protected>} />
        <Route path="/viewers" element={<Protected><Viewers /></Protected>} />
        <Route path="/moments" element={<Protected><Moments /></Protected>} />
        <Route path="/moments/new" element={<Protected><NewMoment /></Protected>} />
        <Route path="/verify" element={<Protected><Verify /></Protected>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <WelcomeTour open={tourOpen} onClose={() => setTourOpen(false)} />
      <SecondOpenPushPrompt user={user} />
    </Suspense>
  );
}

export default function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw-push.js").catch(() => {});
    }
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CallProvider>
            <LightboxProvider>
              <DesktopFrame>
                <DesktopSidebar />
                <DesktopRightRail />
                <Shell />
              </DesktopFrame>
              <CallOverlay />
              <IOSInstallBanner />
              <DesktopInstallPrompt />
              <AdSenseLoader />
              <Toaster position="top-center" richColors />
            </LightboxProvider>
          </CallProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
